import { create } from 'zustand';

type DrawerMode = 'full' | 'compact';

/**
 * Canale mostrato nel drawer "Messaggi". Un solo drawer per tutte le
 * conversazioni (decisione Luca 02/09/2026): `all` e' la vista iniziale con la
 * lista unificata di ogni canale ed etichetta di provenienza; WhatsApp,
 * Instagram e Messenger (messaggistica Meta) e la chat di team sono le schede.
 */
export type DrawerChannel = 'all' | 'whatsapp' | 'instagram' | 'messenger' | 'team';

/** Conversazione selezionata nella vista "Tutte": la provenienza dice quale thread aprire. */
export interface UnifiedSelection {
  source: 'whatsapp' | 'meta' | 'team';
  id: string;
}

interface WhatsAppDrawerState {
  isOpen: boolean;
  mode: DrawerMode;
  isInitialized: boolean;
  channel: DrawerChannel;
  allSelection: UnifiedSelection | null;

  // Richiesta di apertura "mirata" da fuori (es. azione Chat sulla card kanban):
  // il drawer consuma pendingConfigId (switch numero se diverso dall'attivo) e
  // pendingChatPhone (selezione chat per numero normalizzato, senza @suffix).
  pendingConfigId: string | null;
  pendingChatPhone: string | null;
  /** Nome da dare alla conversazione se col numero non c'e' ancora una chat. */
  pendingChatName: string | null;
  pendingMetaConversationId: string | null;
  pendingTeamChatId: string | null;

  open: () => void;
  openWithChat: (phoneDigits: string, configId?: string | null, chatName?: string | null) => void;
  /** Apre il drawer su un canale preciso (usato dall'icona nell'header e dai deep link). */
  openChannel: (channel: DrawerChannel) => void;
  /** Apre il drawer direttamente su una conversazione Meta (es. dalla scheda cliente). */
  openMetaConversation: (channel: 'instagram' | 'messenger', conversationId: string) => void;
  /** Apre il drawer sulla chat di team (eventualmente su una chat precisa). */
  openTeamChat: (chatId?: string | null) => void;
  clearPendingConfig: () => void;
  clearPendingChat: () => void;
  clearPendingMetaConversation: () => void;
  clearPendingTeamChat: () => void;
  close: () => void;
  toggle: () => void;
  setMode: (mode: DrawerMode) => void;
  setChannel: (channel: DrawerChannel) => void;
  setAllSelection: (selection: UnifiedSelection | null) => void;
  markInitialized: () => void;
}

const STORAGE_KEY = 'whatsapp_drawer_mode';

function getStoredMode(): DrawerMode {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'full' || stored === 'compact') return stored;
  } catch {}
  return 'full';
}

export const useWhatsAppDrawerStore = create<WhatsAppDrawerState>((set) => ({
  isOpen: false,
  mode: getStoredMode(),
  isInitialized: false,
  channel: 'all',
  allSelection: null,
  pendingConfigId: null,
  pendingChatPhone: null,
  pendingChatName: null,
  pendingMetaConversationId: null,
  pendingTeamChatId: null,

  open: () => set({ isOpen: true }),
  openWithChat: (phoneDigits, configId, chatName) => set({
    isOpen: true,
    channel: 'whatsapp',
    pendingChatPhone: phoneDigits,
    pendingChatName: chatName ?? null,
    pendingConfigId: configId ?? null,
  }),
  openChannel: (channel) => set({ isOpen: true, channel }),
  openMetaConversation: (channel, conversationId) => set({
    isOpen: true,
    channel,
    pendingMetaConversationId: conversationId,
  }),
  openTeamChat: (chatId) => set({
    isOpen: true,
    channel: 'team',
    pendingTeamChatId: chatId ?? null,
  }),
  clearPendingConfig: () => set({ pendingConfigId: null }),
  clearPendingChat: () => set({ pendingChatPhone: null, pendingChatName: null }),
  clearPendingMetaConversation: () => set({ pendingMetaConversationId: null }),
  clearPendingTeamChat: () => set({ pendingTeamChatId: null }),
  close: () => set({ isOpen: false }),
  toggle: () => set((s) => ({ isOpen: !s.isOpen })),

  setMode: (mode) => {
    localStorage.setItem(STORAGE_KEY, mode);
    set({ mode });
  },
  setChannel: (channel) => set({ channel }),
  setAllSelection: (selection) => set({ allSelection: selection }),

  markInitialized: () => set({ isInitialized: true }),
}));
